import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { fireSubmitSuggestionFeedbackUnifiedAnalytics } from '../ui/utils/analytics';

import type { SubmitSuggestionFeedbackAction } from './types';

/**
 * Submit user feedback (good / bad) for a suggestion.
 *
 * Fires the canonical unified `aiFeedback submitted` event so the vote is
 * captured by the AI thumbs-feedback pipeline. The event reuses the originating
 * interaction's instrumentation ID (persisted in plugin state by `startThinking`)
 * so the feedback correlates back to the suggestion's `aiInteraction initiated`
 * event. Returns `false` (and fires nothing) when the target suggestion no
 * longer exists.
 *
 * TODO(EDITOR-8150): Only analytics are emitted today. Wire up the real
 * feedback destination (endpoint / product provider callback) once finalised.
 */
export const submitSuggestionFeedbackAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => SubmitSuggestionFeedbackAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(id, sentiment, options): boolean => {
		const pluginState = api?.aiSuggestions?.sharedState.currentState();
		const suggestion = pluginState?.suggestionById.get(id);
		if (!suggestion) {
			return false;
		}
		// Reuse the originating interaction's instrumentation ID so feedback can be
		// joined to the `aiInteraction initiated` event. Fall back to a fresh UUID
		// if it is missing (e.g. suggestions dispatched without going through
		// `startThinking`) so the event still carries a valid, self-consistent ID.
		const interactionID = pluginState?.interactionID ?? crypto.randomUUID();
		fireSubmitSuggestionFeedbackUnifiedAnalytics(
			api?.analytics?.actions,
			sentiment,
			interactionID,
			options?.interactionPoint ?? 'card',
		);
		return true;
	};
